import { getAllOrganizationsUser } from "api/organizations";
import { getAllOrg, getAllOrganizations } from "api/graphQL_organizations";
import { useEffect, useState, useContext, useRef } from "react";
import toast from "react-hot-toast";
import CreateOrgModal from "./components/CreateOrgModal";
import OrgTable from "./components/OrganizaionsList";
import { getSingleRole } from "api/graphQL_role";
import { getAllGatewaysSecond } from "api/graphQL_gateways";
import { ClientContext } from "ClientProvider";
import { useLocation, useNavigate } from "react-router-dom";

export type OrgRowObj = {
  name: string;
  email: string;
  phone: string;
  address: string;
  description: string;
  website: string;
  created_at: string;
  id: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  payment_methods?: string[];
  gateways?: [];
  clients?: [];
};

export type AllGatewayObj = {
  name: string;
  id: string;
};

const Organizations = () => {
  const [organizations, setOrganizations] = useState<OrgRowObj[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>();
  const [isDataLoading, setIsDataLoading] = useState<boolean>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [totalpage, setTotalPage] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [allGateways, setAllGateways] = useState<AllGatewayObj[]>([]);
  const { singleRoleData, setSingleRoleData } = useContext(ClientContext);
  const [searchTexts, setSearchTexts] = useState<string>("");
  const location = useLocation();
  const navigate = useNavigate();
  const [allOrg, setAllOrg] = useState<any[]>([]);

  const prevValues = useRef<{ searchTexts: string }>({
    searchTexts: "__init__",
  });

  const fetchOrganizations = () => {
    setIsDataLoading(true);
    if (pageSize === null || pageSize === undefined) {
      setIsDataLoading(false);
      return;
    }

    let finalSearchText = "";
    if (location.state) {
      if (location.state?.search) {
        finalSearchText = location.state.search;
        const searchTexts = location.state.search;
        prevValues.current = { searchTexts };
        navigate(".", { replace: true, state: null });
      }
    } else {
      finalSearchText = searchTexts;
    }

    getAllOrganizations(page, pageSize, finalSearchText)
      .then((data) => {
        setOrganizations(
          data?.[0]?.map((ele: any) => {
            return { ...ele, id: ele.id };
          })
        );
        setTotalPage(data?.[1]?.total_pages);
        setTotalItems(data?.[1]?.total_items);
        setCurrentPage(data?.[1]?.current_page);
      })
      .catch((err) => {
        setOrganizations([]);
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  };

  const fetchAllGateways = () => {
    getAllGatewaysSecond()
      .then((data) => {
        setAllGateways(data?.[0] ? data : []);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ??
            "Something went wrong while fetching AllGateways"
        );
      });
  };

  useEffect(() => {
    const hasChanged = prevValues.current.searchTexts !== searchTexts;
    if (!hasChanged) {
      return;
    }
    fetchOrganizations();
  }, [page, searchTexts, pageSize]);

  const fetchAllOrganizations = () => {
    getAllOrg()
      .then((data) => {
        setAllOrg(data);
      })
      .catch((err) => {
        setAllOrg([]);
      });
  };

  useEffect(() => {
    fetchAllGateways();
    fetchAllOrganizations();
  }, []);

  const serchboxValueChange = async (searchTerm: string) => {
    const search_txt = searchTerm || "";
    if (search_txt !== searchTexts && searchTexts !== "") {
      setSearchTexts(search_txt);
      setPage(1);
      setCurrentPage(1);
    } else {
      setSearchTexts(search_txt);
    }
  };

  useEffect(() => {
    setPageSize(50);
  }, [searchTexts]);

  return (
    <div className="org-root">
      {/* Page header */}
      <div className="org-page-header">
        <div className="org-page-title-block">
          <div className="org-page-icon">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <div>
            <h1 className="org-page-heading">Organizations</h1>
            <p className="org-page-sub">
              Manage your organizations and gateway assignments
            </p>
          </div>
        </div>
        <div className="org-page-actions">
          <CreateOrgModal
            fetchOrganizations={fetchOrganizations}
            fetchAllOrganizations={fetchAllOrganizations}
            allGateways={allGateways?.filter((f: any) => f?.is_active)}
            roleData={singleRoleData}
          />
        </div>
      </div>

      {/* Table */}
      <OrgTable
        tableData={organizations}
        currentPage={currentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        page={page}
        setPage={setPage}
        totalItems={totalItems}
        totalpage={totalpage}
        fetchOrganizations={fetchOrganizations}
        fetchAllOrganizations={fetchAllOrganizations}
        allGateways={allGateways?.filter((f: any) => f?.is_active)}
        isLoading={isDataLoading}
        roleData={singleRoleData}
        onValueChange={serchboxValueChange}
        allOrg={allOrg}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .org-root {
          font-family: 'Plus Jakarta Sans', sans-serif;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .org-page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }
        .org-page-title-block { display: flex; align-items: center; gap: 14px; }
        .org-page-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: linear-gradient(135deg, #eff6ff, #dbeafe);
          border: 1.5px solid #bfdbfe;
          display: flex; align-items: center; justify-content: center;
          color: #2563eb; flex-shrink: 0;
        }
        .org-page-heading {
          font-size: 20px; font-weight: 800; color: #0f172a;
          letter-spacing: -0.4px; margin-bottom: 2px; line-height: 22px;
        }
        .org-page-sub { font-size: 13px; color: #94a3b8; font-weight: 500; }
        .org-page-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }

        @media (max-width: 600px) {
          .org-root { padding: 14px; gap: 14px; }
          .org-page-header { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default Organizations;